import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  Zap, ShieldCheck, Sparkles, MessagesSquare, Clock, BarChart3,
  CheckCircle2, ArrowRight, Eye, EyeOff, Bot, X, Mail
} from 'lucide-react';
import { MarcaRiia } from '../components/LogoRiia';

const SITE_MARKETING = 'https://disparaia.net';

export default function Login() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [modalRecuperar, setModalRecuperar] = useState(false);
  const [emailRecuperar, setEmailRecuperar] = useState('');
  const [enviandoRecuperar, setEnviandoRecuperar] = useState(false);
  const { login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('sessao') === 'expirada') {
      toast('Sessão encerrada por inatividade. Faça login novamente.', {
        icon: '🔒',
        duration: 5000,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRecuperarSenha(e) {
    e.preventDefault();
    if (!emailRecuperar.trim()) return;
    setEnviandoRecuperar(true);
    try {
      const { data } = await api.post('/auth/recuperar-senha', { email: emailRecuperar.trim().toLowerCase() });
      toast.success(data.mensagem || t('login.recuperar_sucesso'));
      setModalRecuperar(false);
      setEmailRecuperar('');
    } catch (err) {
      toast.error(err.response?.data?.erro || t('login.recuperar_erro'));
    } finally {
      setEnviandoRecuperar(false);
    }
  }

  const WHATSAPP_VENDAS = 'https://wa.me/5561991093750?text=' +
    encodeURIComponent(t('login.whatsapp_vendas_msg'));

  const VANTAGENS = [
    { icon: <Bot size={22} />,           titulo: t('login.vantagens.secretaria_titulo'), texto: t('login.vantagens.secretaria_texto') },
    { icon: <Sparkles size={22} />,      titulo: t('login.vantagens.ia_titulo'),         texto: t('login.vantagens.ia_texto') },
    { icon: <Clock size={22} />,         titulo: t('login.vantagens.pacing_titulo'),     texto: t('login.vantagens.pacing_texto') },
    { icon: <ShieldCheck size={22} />,   titulo: t('login.vantagens.janela_titulo'),     texto: t('login.vantagens.janela_texto') },
    { icon: <MessagesSquare size={22} />,titulo: t('login.vantagens.canais_titulo'),     texto: t('login.vantagens.canais_texto') },
    { icon: <BarChart3 size={22} />,     titulo: t('login.vantagens.tempo_real_titulo'), texto: t('login.vantagens.tempo_real_texto') },
    { icon: <Zap size={22} />,           titulo: t('login.vantagens.import_titulo'),     texto: t('login.vantagens.import_texto') },
  ];

  const PLANOS = [
    {
      nome: 'Freemium', preco: '0', periodo: '/mês', gratis: true, destaque: false,
      descricao: t('login.planos.freemium_descricao'),
      recursos: t('login.planos.freemium_recursos'),
      cta: t('login.planos.freemium_cta'),
      href: `${SITE_MARKETING}/?plano=freemium`,
    },
    {
      nome: 'Starter', preco: '97', periodo: '/mês', destaque: false,
      descricao: t('login.planos.starter_descricao'),
      recursos: t('login.planos.starter_recursos'),
      cta: t('login.planos.starter_cta'),
      href: `${SITE_MARKETING}/?plano=starter&periodo=mensal`,
    },
    {
      nome: 'Essencial', preco: '347', periodo: '/mês', destaque: false,
      descricao: t('login.planos.essencial_descricao'),
      recursos: t('login.planos.essencial_recursos'),
      cta: t('login.planos.essencial_cta'),
      href: `${SITE_MARKETING}/?plano=essencial&periodo=mensal`,
    },
    {
      nome: 'Profissional', preco: '797', periodo: '/mês', destaque: true,
      descricao: t('login.planos.pro_descricao'),
      recursos: t('login.planos.pro_recursos'),
      cta: t('login.planos.pro_cta'),
      href: `${SITE_MARKETING}/?plano=pro&periodo=mensal`,
    },
    {
      nome: 'Empresarial', consulta: true, destaque: false,
      descricao: t('login.planos.enterprise_descricao'),
      recursos: t('login.planos.enterprise_recursos'),
      cta: t('login.planos.enterprise_cta'),
      href: WHATSAPP_VENDAS,
    },
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    setCarregando(true);
    try {
      await login(email.trim().toLowerCase(), senha);
      navigate('/');
    } catch (err) {
      const status = err.response?.status;
      const msgBackend = err.response?.data?.erro || err.response?.data?.message;
      let msg;
      if (status === 401 || status === 400) {
        msg = msgBackend || t('login.erro_credenciais');
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        msg = t('login.erro_conexao');
      } else if (status >= 500) {
        msg = t('login.erro_servidor');
      } else {
        msg = msgBackend || t('login.erro_generico', { status: status || '' });
      }
      toast.error(msg);
    } finally {
      setCarregando(false);
    }
  }

  function scrollPara(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        radial-gradient(900px 500px at 100% 0%, rgba(22, 163, 74,0.18), transparent 60%),
        radial-gradient(700px 500px at 0% 100%, rgba(139,92,246,0.18), transparent 60%),
        linear-gradient(180deg, #081832 0%, #0b1f3f 100%)
      `,
      color: '#e6e6f0'
    }}>
      {/* Header simples */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1.25rem clamp(1rem, 4vw, 3rem)',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#fff',
            display: 'grid', placeItems: 'center', overflow: 'hidden',
            boxShadow: '0 6px 16px rgba(22, 163, 74,0.25)'
          }}><MarcaRiia size={30} fundo="#ffffff" /></div>
          <div style={{ lineHeight: 1.1 }}>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '0.02em' }}>RIIA</div>
            <div style={{ fontSize: 11, color: '#9aa0b6' }}>{t('login.marca_sub')}</div>
          </div>
        </div>
        <nav style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <button onClick={() => scrollPara('vantagens')} style={navLinkStyle}>{t('login.nav_vantagens')}</button>
          <button onClick={() => scrollPara('planos')} style={navLinkStyle}>{t('login.nav_planos')}</button>
          <button onClick={() => scrollPara('login')}
            style={{
              ...navLinkStyle, background: 'rgba(255,255,255,0.08)',
              padding: '8px 16px', borderRadius: 999
            }}>
            {t('login.nav_entrar')}
          </button>
        </nav>
      </header>

      {/* HERO + Login lado a lado */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 420px)',
        gap: 'clamp(2rem, 5vw, 4rem)',
        alignItems: 'center',
        padding: 'clamp(2rem, 6vw, 4rem) clamp(1rem, 4vw, 3rem) clamp(3rem, 7vw, 5rem)',
        maxWidth: 1280, margin: '0 auto'
      }} className="hero-grid">
        <div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 999,
            background: 'rgba(22, 163, 74,0.18)', border: '1px solid rgba(22, 163, 74,0.4)',
            color: '#bbf7d0', fontSize: 12, fontWeight: 600, marginBottom: 18
          }}>
            <Sparkles size={13} /> {t('login.hero_badge')}
          </span>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.4rem)', fontWeight: 700,
            margin: '0 0 1rem', letterSpacing: '-0.02em', lineHeight: 1.1, color: '#fff'
          }}>
            {t('login.hero_titulo_1')}{' '}
            <span style={{
              background: 'linear-gradient(135deg, #4ade80, #4ade80)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>{t('login.hero_titulo_destaque')}</span>
          </h1>
          <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: '#b9bdd1', margin: '0 0 1.75rem', maxWidth: 580, lineHeight: 1.5 }}>
            {t('login.hero_sub')}
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: '2rem' }}>
            <a href={`${SITE_MARKETING}/?plano=freemium`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '13px 22px', borderRadius: 12,
              background: 'linear-gradient(135deg, #16a34a, #13305f)',
              color: '#fff', fontWeight: 600, fontSize: 15,
              boxShadow: '0 12px 28px rgba(22, 163, 74,0.4)'
            }}>
              {t('login.hero_cta_gratis')} <ArrowRight size={16} />
            </a>
            <button onClick={() => scrollPara('planos')} style={{
              padding: '13px 22px', borderRadius: 12,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer'
            }}>
              {t('login.hero_cta_planos')}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 'clamp(1rem, 4vw, 2.5rem)', flexWrap: 'wrap', color: '#9aa0b6', fontSize: 13 }}>
            <Stat valor="98%" label={t('login.stat_entrega')} />
            <Stat valor="5×" label={t('login.stat_respostas')} />
            <Stat valor="+300" label={t('login.stat_negocios')} />
            <Stat valor={t('login.stat_freemium_valor')} label={t('login.stat_freemium')} />
          </div>
        </div>

        {/* Login form (também serve como âncora #login) */}
        <div id="login" style={{
          background: 'rgba(255,255,255,0.98)',
          padding: '2.25rem',
          borderRadius: 16,
          color: 'var(--color-text)',
          boxShadow: '0 24px 64px rgba(15,17,32,0.45)',
          border: '1px solid rgba(255,255,255,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11, background: '#fff',
              display: 'grid', placeItems: 'center', overflow: 'hidden',
              boxShadow: '0 8px 20px rgba(22, 163, 74,0.3)'
            }}><MarcaRiia size={34} fundo="#ffffff" /></div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>{t('login.form_titulo')}</h2>
              <p style={{ color: '#8a8f9f', fontSize: 12, margin: '2px 0 0' }}>{t('login.form_sub')}</p>
            </div>
          </div>
          <p style={{ color: '#5b6173', margin: '1rem 0 1.5rem', fontSize: 14 }}>
            {t('login.form_ja_cliente')}
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={inputLabel}>{t('login.form_email')}</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com" required
                style={inputField}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={inputLabel}>{t('login.form_senha')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senha} onChange={e => setSenha(e.target.value)}
                  placeholder="••••••••" required
                  style={{ ...inputField, paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(v => !v)}
                  aria-label={mostrarSenha ? t('login.form_ocultar_senha') : t('login.form_mostrar_senha')}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 6, display: 'grid', placeItems: 'center', color: '#8a8f9f'
                  }}
                >
                  {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={carregando} style={{
              width: '100%', padding: 12, color: '#fff',
              background: carregando ? '#9aa0b6'
                : 'linear-gradient(135deg, #13305f 0%, #1d4480 100%)',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: carregando ? 'not-allowed' : 'pointer',
              boxShadow: '0 10px 24px rgba(26,26,46,0.25)'
            }}>
              {carregando ? t('login.form_entrando') : t('login.form_entrar')}
            </button>

            <p style={{ marginTop: 12, fontSize: 12, textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setModalRecuperar(true)}
                style={{ background: 'none', border: 'none', color: '#12833b', fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                {t('login.form_esqueci_senha')}
              </button>
            </p>
          </form>

          <p style={{ marginTop: 18, fontSize: 12, color: '#8a8f9f', textAlign: 'center' }}>
            {t('login.form_sem_conta')}{' '}
            <button onClick={() => scrollPara('planos')} style={{
              background: 'none', border: 'none', color: '#12833b', fontWeight: 600, cursor: 'pointer', padding: 0
            }}>
              {t('login.form_ver_planos')}
            </button>
          </p>
        </div>
      </section>

      {/* Modal de recuperação de senha */}
      {modalRecuperar && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setModalRecuperar(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: '2rem',
            width: '100%', maxWidth: 420, color: '#13305f',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Mail size={20} color="#16a34a" />
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t('login.recuperar_titulo')}</h3>
              </div>
              <button onClick={() => setModalRecuperar(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 1.25rem' }}>
              {t('login.recuperar_descricao')}
            </p>
            <form onSubmit={handleRecuperarSenha}>
              <input
                type="email"
                value={emailRecuperar}
                onChange={e => setEmailRecuperar(e.target.value)}
                placeholder="seu@email.com"
                required
                style={{
                  width: '100%', padding: '11px 13px', borderRadius: 10,
                  border: '1px solid #e6e8ef', fontSize: 15,
                  boxSizing: 'border-box', background: '#fafbfd', marginBottom: 12
                }}
              />
              <button type="submit" disabled={enviandoRecuperar} style={{
                width: '100%', padding: 12, color: '#fff',
                background: enviandoRecuperar ? '#9aa0b6' : 'linear-gradient(135deg, #16a34a, #13305f)',
                border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
                cursor: enviandoRecuperar ? 'not-allowed' : 'pointer'
              }}>
                {enviandoRecuperar ? t('login.recuperar_enviando') : t('login.recuperar_enviar')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* VANTAGENS */}
      <section id="vantagens" style={{
        padding: 'clamp(3rem, 6vw, 5rem) clamp(1rem, 4vw, 3rem)',
        background: 'rgba(0,0,0,0.25)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 700, margin: '0 0 0.5rem',
            color: '#fff', letterSpacing: '-0.02em', textAlign: 'center'
          }}>
            {t('login.vantagens_titulo')}
          </h2>
          <p style={{ color: '#b9bdd1', textAlign: 'center', margin: '0 auto 2.5rem', maxWidth: 640, fontSize: 16 }}>
            {t('login.vantagens_sub')}
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1.25rem'
          }}>
            {VANTAGENS.map(v => (
              <div key={v.titulo} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14, padding: '1.5rem',
                transition: 'transform 200ms var(--ease), border-color 200ms var(--ease)'
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(22, 163, 74,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: 'linear-gradient(135deg, rgba(22, 163, 74,0.25), rgba(139,92,246,0.25))',
                  border: '1px solid rgba(22, 163, 74,0.35)',
                  display: 'grid', placeItems: 'center', color: '#bbf7d0',
                  marginBottom: 14
                }}>
                  {v.icon}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px', color: '#fff' }}>{v.titulo}</h3>
                <p style={{ fontSize: 14, color: '#b9bdd1', margin: 0, lineHeight: 1.55 }}>{v.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" style={{
        padding: 'clamp(3rem, 6vw, 5rem) clamp(1rem, 4vw, 3rem)'
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 700, margin: '0 0 0.5rem',
            color: '#fff', letterSpacing: '-0.02em', textAlign: 'center'
          }}>
            {t('login.planos_titulo')}
          </h2>
          <p style={{ color: '#b9bdd1', textAlign: 'center', margin: '0 auto 2.5rem', maxWidth: 600, fontSize: 16 }}>
            {t('login.planos_sub')}
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
            alignItems: 'stretch',
          }}
            className="planos-grid"
          >
            {PLANOS.map(p => (
              <div key={p.nome} style={{
                background: p.destaque
                  ? 'linear-gradient(160deg, rgba(22, 163, 74,0.18), rgba(139,92,246,0.18))'
                  : 'rgba(255,255,255,0.04)',
                border: p.destaque ? '1.5px solid #4ade80' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: 16, padding: '1.75rem',
                position: 'relative',
                boxShadow: p.destaque ? '0 16px 40px rgba(22, 163, 74,0.25)' : 'none',
                display: 'flex', flexDirection: 'column',
              }}>
                {p.destaque && (
                  <span style={{
                    position: 'absolute', top: -12, left: 20,
                    padding: '4px 12px', borderRadius: 999,
                    background: 'linear-gradient(135deg, #16a34a, #13305f)',
                    color: '#fff', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.04em', textTransform: 'uppercase'
                  }}>
                    {t('login.plano_popular')}
                  </span>
                )}
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 2px', color: '#fff' }}>{p.nome}</h3>
                  {p.descricao && <p style={{ fontSize: 12, color: '#9aa0b6', margin: '0 0 8px' }}>{p.descricao}</p>}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    {!p.gratis && !p.consulta && <span style={{ fontSize: 16, color: '#b9bdd1', fontWeight: 600 }}>R$</span>}
                    <span style={{
                      fontSize: p.consulta ? 26 : 38, fontWeight: 800,
                      color: p.gratis ? '#22c55e' : p.consulta ? '#4ade80' : '#fff',
                      letterSpacing: '-0.02em'
                    }}>
                      {p.gratis ? t('login.plano_gratis') : p.consulta ? t('login.plano_sob_consulta') : p.preco}
                    </span>
                    {p.periodo && !p.gratis && !p.consulta && <span style={{ color: '#9aa0b6', fontSize: 14 }}>{p.periodo}</span>}
                  </div>
                  {p.gratis && (
                    <span style={{ fontSize: 13, color: '#9aa0b6' }}>{t('login.plano_para_sempre')}</span>
                  )}
                  {p.consulta && (
                    <span style={{ fontSize: 13, color: '#9aa0b6' }}>{t('login.plano_personalizado')}</span>
                  )}
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.75rem', flex: 1 }}>
                  {p.recursos.map(r => (
                    <li key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: '#b9bdd1', padding: '6px 0' }}>
                      <CheckCircle2 size={16} color="#22c55e" style={{ flexShrink: 0, marginTop: 2 }} />
                      {r}
                    </li>
                  ))}
                </ul>

                <a
                  href={p.href}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '12px', borderRadius: 10, width: '100%',
                    background: p.destaque
                      ? 'linear-gradient(135deg, #16a34a, #13305f)'
                      : p.gratis ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.08)',
                    color: '#fff', fontWeight: 600, fontSize: 14,
                    border: p.destaque ? 'none' : p.gratis ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(255,255,255,0.16)',
                    boxShadow: p.destaque ? '0 10px 24px rgba(22, 163, 74,0.35)' : 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  {p.cta} <ArrowRight size={15} />
                </a>
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', color: '#9aa0b6', fontSize: 12, marginTop: '1.5rem' }}>
            {t('login.planos_rodape')}
          </p>
        </div>
      </section>

      {/* CTA final */}
      <section style={{
        padding: 'clamp(2rem, 5vw, 4rem) clamp(1rem, 4vw, 3rem)',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: 720, margin: '0 auto',
          padding: 'clamp(2rem, 4vw, 3rem)',
          borderRadius: 18,
          background: 'linear-gradient(135deg, rgba(22, 163, 74,0.15), rgba(139,92,246,0.15))',
          border: '1px solid rgba(22, 163, 74,0.35)'
        }}>
          <h3 style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontWeight: 700, margin: '0 0 0.75rem', color: '#fff' }}>
            {t('login.cta_titulo')}
          </h3>
          <p style={{ color: '#b9bdd1', margin: '0 0 1.5rem', fontSize: 15 }}>
            {t('login.cta_sub')}
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={`${SITE_MARKETING}/?plano=freemium`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '13px 24px', borderRadius: 12,
              background: 'linear-gradient(135deg, #16a34a, #13305f)',
              color: '#fff', fontWeight: 600, fontSize: 15,
              boxShadow: '0 12px 28px rgba(22, 163, 74,0.4)'
            }}>
              {t('login.cta_gratis')} <ArrowRight size={16} />
            </a>
            <a href={WHATSAPP_VENDAS} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '13px 24px', borderRadius: 12,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.16)',
              color: '#fff', fontWeight: 600, fontSize: 15
            }}>
              {t('login.cta_vendas')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '1.75rem clamp(1rem, 4vw, 3rem)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 12, color: '#8a8f9f', fontSize: 13
      }}>
        <span>{t('login.footer_direitos', { ano: new Date().getFullYear() })}</span>
        <div style={{ display: 'flex', gap: 18 }}>
          <a href={SITE_MARKETING} style={{ color: '#b9bdd1' }}>{t('login.footer_site')}</a>
          <a href={WHATSAPP_VENDAS} target="_blank" rel="noopener noreferrer" style={{ color: '#b9bdd1' }}>{t('login.footer_suporte')}</a>
          <a href={`${SITE_MARKETING}/?plano=starter&periodo=anual`} style={{ color: '#b9bdd1' }}>{t('login.footer_assinar')}</a>
        </div>
      </footer>

      <style>{`
        @media (max-width: 880px) {
          .hero-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 960px) {
          .planos-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 520px) {
          .planos-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const navLinkStyle = {
  background: 'none', border: 'none', color: '#c7cad6',
  fontWeight: 500, fontSize: 14, cursor: 'pointer'
};

const inputLabel = {
  fontSize: 12.5, color: '#5b6173', display: 'block',
  marginBottom: 6, fontWeight: 500
};

const inputField = {
  width: '100%', padding: '11px 13px', borderRadius: 10,
  border: '1px solid #e6e8ef', fontSize: 16,
  boxSizing: 'border-box', background: '#fafbfd'
};

function Stat({ valor, label }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{valor}</div>
      <div style={{ fontSize: 12, color: '#9aa0b6' }}>{label}</div>
    </div>
  );
}
